import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem } from 'polished';
import { TextP } from '../../Defaults/Text';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { Button, ButtonLink } from './Button';
import { __ } from 'react-i18n';
import { WebP } from '../../Defaults/WebP';

export interface Props {
  url: string;
  title: string;
  imageSrc: string;
  imageMobileSrc: string;
  alt?: string;
  buttonColor?: string;
  bgColor?: string;
  clickHandle?: (name: string) => void;
  image: string;
  descr: string;
}

function Card({
  imageSrc,
  imageMobileSrc,
  url,
  title,
  alt,
  buttonColor,
  bgColor,
  clickHandle,
  image,
  descr,
}: Props) {
  const clickHandleWrapper = (event: React.MouseEvent): void => {
    if (clickHandle) {
      clickHandle(title);
    }
  };

  return (
    <Wrapper bg={bgColor}>
      <ImageWrapper to={url} onClick={clickHandleWrapper}>
        <Image image={image} alt={alt || ''} width={284} height={194} />
        <ImageMobile
          image={image}
          alt={alt || ''}
          width={284}
          height={120}
          cover={true}
        />
      </ImageWrapper>
      <TextButtonWrapper>
        <TextWrapper>
          <TextLink to={url} onClick={clickHandleWrapper}>
            {title}
          </TextLink>
          {!!descr && (
            <SubText to={url} onClick={clickHandleWrapper}>
              {descr}
            </SubText>
          )}
        </TextWrapper>
        <StyledButton
          bgColor={buttonColor}
          to={url}
          onClick={clickHandleWrapper}
        >
          {__('Viac')}
        </StyledButton>
      </TextButtonWrapper>
    </Wrapper>
  );
}

interface WrapperProps {
  bg?: string;
}

const Wrapper = styled(FlexCol)`
  margin-bottom: ${rem(16)};
  width: 100%;
  max-width: ${rem(360)};
  margin-right: ${rem(32)};
  border-radius: ${rem(4)};
  background: ${(props: WrapperProps) =>
    props.bg ? props.bg : 'rgb(49, 39, 131, 0.04)'};
  overflow: hidden;
  max-height: ${rem(450)};

  &:last-child {
    margin-right: 0;
  }

  ${({ theme }) => theme.mediab.l1300`
    margin-right: ${rem(24)};
    margin-bottom: ${rem(12)};
  `}

  ${({ theme }) => theme.mediab.l925`
     flex-flow: row;
     align-items: center;
     margin: 0;
     margin-bottom: ${rem(16)};
     max-width: initial;

     &:hover {
      transform: initial;
    }
  `}
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
    max-width: ${rem(80)}
  `}
`;

const Image = styled(WebP)`
  width: 100%;
  max-height: ${rem(220)};
  margin-bottom: ${rem(24)};
  border-radius: ${rem(4)} ${rem(4)} 0 0;
  transition: transform 0.25s ease;

  &:hover {
    transform: scale(1.05);
  }
  ${({ theme }) => theme.mediab.l1400`
    margin-bottom: ${rem(12)};
    max-height:${rem(100)};
    display:none
  `}

  ${({ theme }) => theme.mediab.l925`
     margin-bottom: 0;
     width: ${rem(80)};
     height: ${rem(80)};
     border-radius: ${rem(2)}  0 0 ${rem(2)} ;
     object-fit: cover;
  `}
`;

const ImageMobile = styled(WebP)`
  display: none;
  width: 100%;
  max-height: ${rem(120)};
  margin-bottom: ${rem(24)};
  border-radius: ${rem(4)} ${rem(4)} 0 0;
  transition: transform 0.25s ease;

  &:hover {
    transform: scale(1.05);
  }
  ${({ theme }) => theme.mediab.l1400`
    margin-bottom: ${rem(12)};
    display:block
  `}

  ${({ theme }) => theme.mediab.l925`
     margin-bottom: 0;
     width: ${rem(80)};
     min-height: ${rem(80)};
     border-radius: ${rem(2)}  0 0 ${rem(2)} ;
     object-fit: cover;
  `}
`;

const TextButtonWrapper = styled(FlexCol)`
  justify-content: space-between;
  height: 100%;
  max-height: 100%;

  ${({ theme }) => theme.mediab.l925`
     flex-flow: row;
     align-items: center;
     width: 100%;
  `}
`;

const TextWrapper = styled(FlexCol)``;

const TextLink = styled(Link)`
  text-decoration: none;
  font-size: ${rem(30)};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${rem(38)};
  font-family: ${({ theme }) => theme.font.primary};
  margin: 0 ${rem(24)} ${rem(24)};

  &:hover {
    text-decoration: underline;
  }

  ${({ theme }) => theme.mediab.l1300`
     font-size: ${rem(24)}; 
  `}

  ${({ theme }) => theme.mediab.l925`
     font-size: ${rem(18)};
     margin: 0;
  `}
`;

const SubText = styled(TextP)`
  margin: ${rem(-16)} ${rem(24)} ${rem(16)};
  color: ${({ theme }) => theme.colors.menuCartTextColor};

  ${({ theme }) => theme.mediab.l925`
    margin: 0;
    padding-bottom: ${rem(8)};
    padding-right: ${rem(8)};
  `}
`;

interface ButtonProps {
  bgColor?: string;
  theme: any;
}

const StyledButton = styled(ButtonLink)`
  background: ${({ theme, bgColor }: ButtonProps) =>
    bgColor ? bgColor : theme.colors.primary};
  color: ${({ theme }) => theme.color.white};
  border-radius: ${rem(2)};
  max-width: ${rem(112)};
  margin: 0 ${rem(24)} ${rem(24)};
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(90%);
  }

  ${({ theme }) => theme.mediab.l925`
     margin: 0;
     margin-right: ${rem(24)};
  `}

  ${({ theme }) => theme.mediab.m580`
     display: none;
  `}
`;

export { Card };
